import axiosConfig from './axiosConfig';
import get from 'lodash/get';

const normEmail = (value) => String(value ?? '').trim().toLowerCase();

export const getTeamMemberDisplayName = (member) => {
  if (!member) return '—';
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
  return name || member.email || '—';
};

export const getTeamMemberKey = (member) => {
  const email = normEmail(member?.email);
  return String(member?.id ?? member?.userProjectId ?? email ?? '');
};

/** Dedupe team members by email; keeps first occurrence. */
export const dedupeTeamMembersByEmail = (members) => {
  const seen = new Set();
  return (members || []).filter((member) => {
    const email = normEmail(member?.email);
    if (!email) return true;
    if (seen.has(email)) return false;
    seen.add(email);
    return true;
  });
};

/** Team members available to add (not already on this shift log). */
export const filterAvailableTeamMembersForLog = (members, existingEmails = []) => {
  const existing = new Set(existingEmails.map(normEmail).filter(Boolean));
  return dedupeTeamMembersByEmail(members).filter((member) => {
    const email = normEmail(member?.email);
    return !email || !existing.has(email);
  });
};

/** Normalize GET /user-project/{id} payload (array or { rows, count }). */
export const parseProjectTeamMembersResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

/** All project team members (Team tab) — includes accessToDsr flag. */
export async function fetchProjectTeamMembers(projectId) {
  if (!projectId) return [];
  try {
    const res = await axiosConfig.get(`/user-project/${projectId}`);
    const data = get(res, ['data', 'data'], null);
    return parseProjectTeamMembersResponse(data);
  } catch {
    return [];
  }
}

/** Upsert team member with accessToDsr enabled (POST /user-project). */
export async function setTeamMemberDsrListing(member, projectId, accessToDsr = true) {
  if (!member || !projectId) return null;
  const roleId = member.roleId ?? member.role?.id;
  if (!roleId) {
    throw new Error('Job title is missing for this team member.');
  }

  const payload = {
    cognitoUserId: member.cognitoUserId || member.userName || member.username || member.userId || '',
    email: member.email || '',
    firstName: member.firstName || '',
    lastName: member.lastName || '',
    projectId: Number(projectId),
    roleId: Number(roleId),
    accessToDsr: Boolean(accessToDsr),
    accessToTbt: member.accessToTbt ? 1 : 0,
    isGuestUser: Boolean(member.isGuestUser),
    isNormalUser: member.isNormalUser !== false,
    selectedTabs: member.selectedTabs || [],
    tabPermissions: member.tabPermissions || {},
    isNonCognitoUser: Boolean(member.isNonCognitoUser),
    ...(member.welderId ? { welderId: member.welderId } : {}),
  };

  const res = await axiosConfig.post('/user-project', payload);
  return get(res, ['data', 'data'], null);
}

export const buildTechnicianRowFromTeamMember = (member) => {
  const userProjectId = member.id ?? member.userProjectId;
  const roleName = member.roleName || member.role?.name || 'Select Role';
  return {
    id: `pending-${userProjectId || member.email}`,
    name: [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email || '—',
    status: 'Select Status',
    roles: roleName,
    hrs: 0,
    overTime: 0,
    apiData: {
      userEmail: member.email,
      userFirstName: member.firstName,
      userLastName: member.lastName,
      userProjectId,
    },
    _isNew: true,
    _teamMember: member,
  };
};

export const isPendingTechnicianRowId = (id) => String(id ?? '').startsWith('pending-');
